import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { lazyWithReload } from '../utils/lazyWithReload';
import { useParams, useNavigate } from 'react-router-dom';
import * as swimmingService from '../services/swimmingService';
import { AZURE } from '../services/api';
import { formatSwimmingTimeRobust } from '../utils/swimmingFormatters';
import { VUKZ } from '../theme/vukzTheme';
import { useSportsLang } from '../contexts/SportsLangContext';
import ChampionshipPdfExport from '../components/swimming/ChampionshipPdfExport';
const ChampionshipStatsPanel = lazyWithReload(() => import('../components/swimming/ChampionshipStatsPanel'));

// Swimming-specific accent colors (medals, phases)
const SWIM_GOLD = '#d97706';
const SWIM_GOLD_LIGHT = '#fef3c7';

// === SISTEMA DE PONTUAÇÃO PARA CLASSIFICAÇÃO DE CLUBES ===
const POINTS_SYSTEM = {
  1: 9,
  2: 7,
  3: 6,
  4: 5,
  5: 4,
  6: 3,
  7: 2,
  8: 1
};

// === CONFIGURAÇÃO DE DEBUG ===
const DEBUG = {
  logos: false,
  competitions: false,
  events: false,
  results: false,
  clicks: false
};

// === i18n LABELS ===
const LABELS = {
  pt: {
    loadingComp: 'Carregando competição...',
    errorComp: 'Erro ao carregar competição',
    backComps: 'Voltar para Competições',
    competitions: 'Competições',
    tabResults: 'Resultados',
    tabStats: 'Stats',
    loading: 'Carregando...',
    errorResults: 'Erro ao carregar resultados',
    noResults: 'Nenhum resultado',
    noResultsSub: 'Selecione outra prova ou fase',
    noEvents: 'Nenhuma prova disponível',
    loadingStats: 'Carregando estatísticas...',
    labelYear: 'Ano',
    labelGender: 'Sexo',
    labelType: 'Tipo',
    labelStroke: 'Estilo',
    labelDist: 'Distância',
    labelPhase: 'Fase',
    female: 'Feminino',
    male: 'Masculino',
    femShort: 'Fem',
    mascShort: 'Masc',
    individual: 'Individual',
    relay: 'Revezamento',
    colPos: 'Pos',
    colLane: 'Raia',
    colAthlete: 'Atleta',
    colClub: 'Clube',
    colTime: 'Tempo',
    colPts: 'Pts',
    colDiff: 'Dif',
    heat: 'Série',
    events: 'provas',
    compSubtitle: 'Competição de Natação',
  },
  en: {
    loadingComp: 'Loading competition...',
    errorComp: 'Error loading competition',
    backComps: 'Back to Competitions',
    competitions: 'Competitions',
    tabResults: 'Results',
    tabStats: 'Stats',
    loading: 'Loading...',
    errorResults: 'Error loading results',
    noResults: 'No results',
    noResultsSub: 'Select another event or phase',
    noEvents: 'No events available',
    loadingStats: 'Loading statistics...',
    labelYear: 'Year',
    labelGender: 'Gender',
    labelType: 'Type',
    labelStroke: 'Stroke',
    labelDist: 'Distance',
    labelPhase: 'Phase',
    female: 'Female',
    male: 'Male',
    femShort: 'F',
    mascShort: 'M',
    individual: 'Individual',
    relay: 'Relay',
    colPos: 'Pos',
    colLane: 'Lane',
    colAthlete: 'Athlete',
    colClub: 'Club',
    colTime: 'Time',
    colPts: 'Pts',
    colDiff: 'Diff',
    heat: 'Heat',
    events: 'events',
    compSubtitle: 'Swimming Competition',
  },
};

// === FUNÇÃO PARA FORMATAR NOMES DAS PROVAS ===
const formatEventName = (distance, stroke, gender, isRelay) => {
  const strokeMap = {
    'Free': 'Livre',
    'Freestyle': 'Livre',
    'Backstroke': 'Costas',
    'Back': 'Costas',
    'Breaststroke': 'Peito',
    'Breast': 'Peito',
    'Butterfly': 'Borboleta',
    'Fly': 'Borboleta',
    'Medley': 'Medley',
    'IM': 'Medley'
  };

  const strokePt = strokeMap[stroke] || stroke;
  const relayText = isRelay ? ' Revezamento' : '';
  return `${distance}m ${strokePt}${relayText}`;
};

// === MAPEAMENTO DE LOGOS DOS CAMPEONATOS ===
const getCompetitionLogo = (competitionName) => {
  if (!competitionName) return null;
  
  const competitionLogos = {
    'TROFÉU JOSÉ FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFÉU JOSE FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFEU JOSE FINKEL': '/logo campeonatos natacao/josefinkel.jpg',
    'TROFÉU MARIA LENK': '/logo campeonatos natacao/marialenk.jpg',
    'TROFEU MARIA LENK': '/logo campeonatos natacao/marialenk.jpg',
    'PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPÍADAS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'OLIMPIADAS PARIS 2024': '/logo campeonatos natacao/paris2024.png',
    'JUNIOR PAN PACIFIC CHAMPIONSHIPS': '/logo campeonatos natacao/juniorpanpacs.png',
    'AUSTRALIAN SWIMMING TRIALS': '/logo campeonatos natacao/austrao trials.png',
    'TOYOTA NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'US TOYOTA NATIONAL CHAMPIONSHIPS': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'WORLD AQUATICS CHAMPIONSHIPS': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'WORLD CHAMPIONSHIPS': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'DOHA 2024': '/logo campeonatos natacao/wolidchampionshipsdoha2024.png',
    'SINGAPORE 2025': '/logo campeonatos natacao/wolidchampionshipssingapure2025.png',
    'US OPEN': '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
    'CBDA': '/logo campeonatos natacao/cbda.png',
    // Campeonatos/meetings por clube/cidade
    'AMERICANA': '/logo campeonatos natacao/americana.jpg',
    'CURITIBANO': '/logo campeonatos natacao/curitibano.png',
    'CLUBE CURITIBANO': '/logo campeonatos natacao/curitibano.png',
    'INDAIATUBA': '/logo campeonatos natacao/indaiatuba.jpeg',
    'NOVOS CIELOS': '/logo campeonatos natacao/novoscielos.png',
    'PAINEIRAS': '/logo campeonatos natacao/paineras.jpg',
    'CLUBE PAINEIRAS': '/logo campeonatos natacao/paineras.jpg',
    'RECREIO DA JUVENTUDE': '/logo campeonatos natacao/recreiodajuventude.jpg',
    'SÃO CAETANO': '/logo campeonatos natacao/sao caetano.jpeg',
    'SAO CAETANO': '/logo campeonatos natacao/sao caetano.jpeg',
    'SWIM FLORIPA': '/logo campeonatos natacao/swimfloripa.avif',
    'SWIMFLORIPA': '/logo campeonatos natacao/swimfloripa.avif',
    'TIJUCA': '/logo campeonatos natacao/tijuca.png',
    'TIJUCA TÊNIS CLUBE': '/logo campeonatos natacao/tijuca.png',
  };
  
  const normalizedName = competitionName.toUpperCase().trim();
  return competitionLogos[normalizedName] || null;
};

// === MAPEAMENTO DE PAÍSES PARA CÓDIGOS DE BANDEIRAS ===
const getCountryFlagCode = (country) => {
  if (!country) return null;
  
  const countryMap = {
    'BRASIL': 'BRA',
    'BRAZIL': 'BRA',
    'ESTADOS UNIDOS': 'USA',
    'USA': 'USA',
    'UNITED STATES': 'USA',
    'FRANÇA': 'FRA',
    'FRANCE': 'FRA',
    'PARIS': 'FRA', // Para Olimpíadas de Paris
    'AUSTRÁLIA': 'AUS',
    'AUSTRALIA': 'AUS',
    'CATAR': 'QAT',
    'QATAR': 'QAT',
    'DOHA': 'QAT', // Para Mundial de Doha
    'SINGAPURA': 'SGP',
    'SINGAPORE': 'SGP',
    'JAPÃO': 'JPN',
    'JAPAN': 'JPN',
    'CHINA': 'CHN',
    'ESPANHA': 'ESP',
    'SPAIN': 'ESP'
  };
  
  const normalized = country.toUpperCase().trim();
  return countryMap[normalized] || null;
};

// === NOMES DE EXIBIÇÃO ===
const getCompetitionDisplayName = (fullName) => {
  if (!fullName) return fullName;
  
  // Normaliza removendo acentos para matching robusto
  const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const normalized = removeAccents(fullName).toUpperCase().trim();
  
  const displayNames = [
    // Brasileiros principais
    { match: 'MARIA LENK', display: 'Maria Lenk' },
    { match: 'JOSE FINKEL', display: 'José Finkel' },
    { match: 'BRASILEIRO ABSOLUTO DE VERAO', display: 'Maria Lenk' },
    { match: 'BRASILEIRO ABSOLUTO', display: 'Maria Lenk' },
    { match: 'BRASILEIRO JUNIOR DE VERAO', display: 'Brasileiro Júnior Verão' },
    { match: 'BRASILEIRO JUNIOR DE INVERNO', display: 'Brasileiro Júnior Inverno' },
    { match: 'BRASILEIRO JUVENIL DE VERAO', display: 'Brasileiro Juvenil Verão' },
    { match: 'BRASILEIRO JUVENIL DE INVERNO', display: 'Brasileiro Juvenil Inverno' },
    { match: 'BRASILEIRO INFANTIL DE VERAO', display: 'Brasileiro Infantil Verão' },
    { match: 'BRASILEIRO INFANTIL DE INVERNO', display: 'Brasileiro Infantil Inverno' },
    { match: 'SELETIVA MUNDIAL', display: 'Seletiva Mundial' },
    { match: 'BRASIL OPEN', display: 'Brasil Open' },
    // Internacionais
    { match: 'OLIMPIADAS DE PARIS', display: 'Olimpíadas Paris' },
    { match: 'OLYMPIC TEAM TRIALS', display: 'US Olympic Trials' },
    { match: 'WORLD AQUATICS CHAMPIONSHIPS', display: 'Mundial' },
    { match: 'WORLD AQUATICS JUNIOR', display: 'Mundial Júnior' },
    { match: 'AUSTRALIAN SWIMMING TRIALS', display: 'Australian Trials' },
    { match: 'TOYOTA NATIONAL', display: 'US Nationals' },
    { match: 'TYR PRO SWIM', display: 'TYR Pro Swim' },
    { match: 'EUROPEAN AQUATICS', display: 'Europeu' },
  ];
  
  for (const { match, display } of displayNames) {
    if (normalized.includes(match)) return display;
  }
  
  // Fallback: remove ano do final
  return fullName.replace(/\s*\d{4}\s*$/g, '').replace(/\s*\(.*?\)\s*$/g, '').trim();
};

const getCompetitionVenue = (fullName, country) => {
  if (!fullName) return '';
  
  const venues = {
    'PARIS 2024': 'Paris, França',
    'OLIMPIADAS DE PARIS': 'Paris, França',
    'AUSTRALIAN SWIMMING TRIALS': 'Brisbane, Austrália',
    'WORLD AQUATICS CHAMPIONSHIPS': 'Doha, Catar',
    'DOHA 2024': 'Doha, Catar',
    'SINGAPORE 2025': 'Singapura',
    'TOYOTA NATIONAL CHAMPIONSHIPS': 'Minneapolis, EUA',
    'TROFEU MARIA LENK': 'Rio de Janeiro, Brasil',
    'TROFEU JOSE FINKEL': 'São Paulo, Brasil',
  };
  
  const normalized = fullName.toUpperCase().trim();
  if (venues[normalized]) return venues[normalized];
  
  for (const [key, value] of Object.entries(venues)) {
    if (normalized.includes(key)) return value;
  }
  
  return country || '';
};

// === COMPONENTE: HEADER DE COMPETIÇÃO (VUKZ — CLEAN, SEM FUNDO PRETO) ===
const SwimmingCompetitionHeader = ({ competition, year, events, onChangeCompetition, allCompetitions }) => {
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;
  const competitionLogo = getCompetitionLogo(competition?.name);
  const competitionName = getCompetitionDisplayName(competition?.name);
  const venue = getCompetitionVenue(competition?.name, competition?.country);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-5">
        {/* Logo */}
        {competitionLogo && (
          <div className="w-16 h-16 rounded-xl border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 bg-white">
            <img 
              src={competitionLogo} 
              alt={competitionName} 
              className="w-12 h-12 object-contain" 
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight truncate">
            {competitionName}
          </h1>
          <p className="text-sm font-medium text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900">{year || competition?.year}</span>
            {venue && <><span className="text-gray-300">|</span><span>{venue}</span></>}
            <span className="text-gray-300">|</span>
            <span>{events.length} {T.events}</span>
          </p>
        </div>

        {/* Seletor de Campeonato */}
        {allCompetitions && allCompetitions.length > 1 && (
          <select
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
            value={competition?.id || ''}
            onChange={(e) => onChangeCompetition && onChangeCompetition(e.target.value)}
          >
            {allCompetitions.map(comp => (
              <option key={comp.id} value={comp.id}>
                {getCompetitionDisplayName(comp.name)} {comp.year || ''}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
};

function SwimmingChampionshipDetailsPage() {
  const { id, year } = useParams();
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;
  
  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  
  // Filtros
  const [selectedYear, setSelectedYear] = useState(year ? Number(year) : new Date().getFullYear());
  const [selectedGender, setSelectedGender] = useState('Feminino');
  const [selectedRelayType, setSelectedRelayType] = useState('individual');
  const [selectedStroke, setSelectedStroke] = useState(null); // 'Livre', 'Costas', etc.
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRound, setSelectedRound] = useState('Final A');
  const [activeTab, setActiveTab] = useState('results'); // 'results' ou 'stats'
  const [standingsMode, setStandingsMode] = useState('current'); // 'current' ou 'overall'
  const [allResults, setAllResults] = useState([]); // Para standings overall (todas as provas);
  const [printAll, setPrintAll] = useState(false); // PDF export mode
  
  // Anos disponíveis (apenas com dados)
  const availableYears = useMemo(() => {
    return [2026, 2025, 2024, 2023];
  }, []);

  // Carregar competição
  useEffect(() => {
    const loadCompetition = async () => {
      try {
        setLoading(true);
        console.log(' Carregando competição ID:', id);
        
        const response = await swimmingService.getCompetitionById(id);
        console.log(' Resposta da API:', response);
        
        // Backend retorna { success, competition }
        const competitionData = response?.competition || response;
        setCompetition(competitionData);
        console.log(' Competição carregada:', competitionData?.name);
        
        // Carregar eventos
        const eventsResponse = await swimmingService.getCompetitionEventsWithPhases(id);
        console.log(' Eventos recebidos:', eventsResponse);
        
        const eventsData = eventsResponse?.events || eventsResponse || [];
        setEvents(eventsData);
        console.log(' Eventos carregados:', eventsData.length);
        
        // Selecionar primeiro evento
        if (eventsData && eventsData.length > 0) {
          const firstEventId = eventsData[0].event_id || eventsData[0].id;
          setSelectedEvent(firstEventId);
          console.log(' Primeiro evento selecionado:', firstEventId);
        }
      } catch (err) {
        console.error(' Erro ao carregar competição:', err);
        setError(err.message || 'Erro ao carregar competição');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      loadCompetition();
    }
  }, [id]);
  
  // Carregar TODOS os resultados para standings overall (apenas finais)
  useEffect(() => {
    const loadAllResults = async () => {
      if (!id) return;
      
      try {
        const { data } = await AZURE.get(`/swimming/results/fast`, {
          params: {
            competition_id: id,
            fase: 'Final'  // Apenas finais para overall
          }
        });
        setAllResults(data?.data || data?.results || data || []);
      } catch (err) {
        console.error(' Erro ao carregar resultados overall:', err);
      }
    };
    
    loadAllResults();
  }, [id]);

  // Filtrar eventos por gênero e tipo
  const filteredEvents = useMemo(() => {
    if (!events || events.length === 0) return [];
    
    return events.filter(event => {
      const matchGender = event.gender === selectedGender || 
                         (event.gender === 'M' && selectedGender === 'Masculino') ||
                         (event.gender === 'F' && selectedGender === 'Feminino');
      const isRelay = event.relay || event.is_relay;
      const matchType = selectedRelayType === 'relay' ? isRelay : !isRelay;
      
      return matchGender && matchType;
    });
  }, [events, selectedGender, selectedRelayType]);

  // Agrupar eventos por estilo para exibição organizada
  const eventsByStroke = useMemo(() => {
    if (!filteredEvents || filteredEvents.length === 0) return {};
    
    const strokeOrder = ['Livre', 'Costas', 'Peito', 'Borboleta', 'Medley'];
    const strokeMap = {
      'Free': 'Livre', 'Freestyle': 'Livre',
      'Backstroke': 'Costas', 'Back': 'Costas',
      'Breaststroke': 'Peito', 'Breast': 'Peito',
      'Butterfly': 'Borboleta', 'Fly': 'Borboleta',
      'Medley': 'Medley', 'IM': 'Medley'
    };
    
    const groups = {};
    filteredEvents.forEach(event => {
      const stroke = strokeMap[event.stroke] || event.stroke || 'Outro';
      if (!groups[stroke]) groups[stroke] = [];
      groups[stroke].push(event);
    });
    
    // Sort groups by strokeOrder, sort events within by distance
    const sorted = {};
    strokeOrder.forEach(s => {
      if (groups[s]) {
        sorted[s] = groups[s].sort((a, b) => 
          (a.distance_meters || a.distance || 0) - (b.distance_meters || b.distance || 0)
        );
      }
    });
    // Add any remaining strokes not in order
    Object.keys(groups).forEach(s => {
      if (!sorted[s]) sorted[s] = groups[s];
    });
    
    return sorted;
  }, [filteredEvents]);

  // Estilos disponíveis (para botões de estilo)
  const availableStrokes = useMemo(() => {
    return Object.keys(eventsByStroke);
  }, [eventsByStroke]);

  // Distâncias disponíveis para o estilo selecionado
  const availableDistances = useMemo(() => {
    if (!selectedStroke || !eventsByStroke[selectedStroke]) return [];
    return eventsByStroke[selectedStroke].map(e => {
      const dist = e.distance_meters || e.distance;
      const isRelay = e.relay || e.is_relay;
      // For relays, format as "4x100" instead of "400"
      let label = `${dist}m`;
      if (isRelay && dist) {
        if (dist === 400 || dist === '400') label = '4x100m';
        else if (dist === 800 || dist === '800') label = '4x200m';
        else if (dist === 200 || dist === '200') label = '4x50m';
        else label = `${dist}m`;
      }
      return { eventId: e.event_id || e.id, distance: dist, label };
    });
  }, [selectedStroke, eventsByStroke]);

  // Auto-selecionar primeiro estilo quando filtros mudam
  useEffect(() => {
    if (availableStrokes.length > 0 && (!selectedStroke || !availableStrokes.includes(selectedStroke))) {
      setSelectedStroke(availableStrokes[0]);
    }
  }, [availableStrokes, selectedStroke]);

  // Auto-selecionar primeira distância quando estilo muda
  useEffect(() => {
    if (availableDistances.length > 0) {
      const currentValid = availableDistances.some(d => d.eventId === selectedEvent);
      if (!currentValid) {
        setSelectedEvent(availableDistances[0].eventId);
      }
    }
  }, [availableDistances, selectedEvent]);

  // Fases disponíveis para o evento selecionado (ordenadas: Eliminatória → Final B → Final A → Final)
  const selectedEventPhases = useMemo(() => {
    if (!selectedEvent || !events.length) return [];
    
    const event = events.find(e => (e.event_id || e.id) === selectedEvent);
    if (!event) return ['Final'];
    
    // Garantir que phases é um array
    if (!event.phases || !Array.isArray(event.phases)) return ['Final'];
    
    // Ordenar fases na ordem correta do campeonato
    const phaseOrder = [
      'Eliminatória', 'Eliminatórias',
      'Semifinal',
      'Final D', 'Final C', 'Final B', 'Final A',
      'Final',
      'Swim-Off', 'Swim-off'
    ];
    
    const sorted = [...event.phases].sort((a, b) => {
      const idxA = phaseOrder.findIndex(p => p === a);
      const idxB = phaseOrder.findIndex(p => p === b);
      return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
    });
    
    return sorted;
  }, [selectedEvent, events]);

  // Garantir evento selecionado válido após filtros
  useEffect(() => {
    if (!filteredEvents || filteredEvents.length === 0) return;
    const hasSelected = filteredEvents.some(e => (e.event_id || e.id) === selectedEvent);
    if (!hasSelected) {
      const firstEventId = filteredEvents[0].event_id || filteredEvents[0].id;
      setSelectedEvent(firstEventId);
    }
  }, [filteredEvents, selectedEvent]);

  // Auto-selecionar melhor fase quando evento muda (Final A > Final > Final B > primeira)
  useEffect(() => {
    if (selectedEventPhases && selectedEventPhases.length > 0) {
      if (!selectedEventPhases.includes(selectedRound)) {
        // Preferência: Final A > Final > Final B > primeira disponível
        const preferred = ['Final A', 'Final', 'Final B'];
        const best = preferred.find(p => selectedEventPhases.includes(p));
        setSelectedRound(best || selectedEventPhases[selectedEventPhases.length - 1]);
      }
    }
  }, [selectedEventPhases, selectedRound]);
  
  // Calcular standings de clubes (pontos 1º-8º)
  const clubStandings = useMemo(() => {
    // Escolhe dataset baseado no modo (current = resultados filtrados, overall = todos os resultados)
    const dataSource = standingsMode === 'overall' ? allResults : results;
    
    if (!dataSource || dataSource.length === 0) return [];
    
    const clubPoints = {};
    
    dataSource.forEach(result => {
      const position = result.position;
      const clubName = result.club_name || 'Independente';
      
      // Só pontua posições 1-8
      if (position >= 1 && position <= 8) {
        const points = POINTS_SYSTEM[position] || 0;
        
        if (!clubPoints[clubName]) {
          clubPoints[clubName] = {
            club_name: clubName,
            club_logo: result.club_logo_url,
            points: 0,
            entries_count: 0,
            positions: []
          };
        }
        
        clubPoints[clubName].points += points;
        clubPoints[clubName].entries_count += 1;
        clubPoints[clubName].positions.push(position);
      }
    });
    
    // Converter para array e ordenar por pontos
    return Object.values(clubPoints)
      .sort((a, b) => b.points - a.points)
      .slice(0, 10); // Top 10
  }, [results, allResults, standingsMode]);
  
  // Calcular stats de atletas (médias, gaps)
  // Carregar resultados
  useEffect(() => {
    const loadResults = async () => {
      if (!selectedEvent) {
        console.log(' Sem evento selecionado, pulando busca de resultados');
        return;
      }
      
      try {
        setLoading(true);
        console.log(' Buscando resultados:', { competition_id: id, event_id: selectedEvent, fase: selectedRound });
        
        const data = await swimmingService.getResults({
          competition_id: id,
          event_id: selectedEvent,
          fase: selectedRound,
          limit: 200
        });
        
        console.log(' Resultados recebidos:', data?.length || 0, 'registros');
        setResults(data || []);
      } catch (err) {
        console.error(' Erro ao carregar resultados:', err);
        setError(err.message || 'Erro ao carregar resultados');
      } finally {
        setLoading(false);
      }
    };
    
    loadResults();
  }, [id, selectedEvent, selectedRound]);
  
  // Função para determinar estilo da posição
  const getPositionStyle = (pos, fase) => {
    const isFinal = fase === 'Final' || fase?.includes('Final');
    const isSemifinal = fase === 'Semifinal' || fase?.includes('Semi');
    
    if (isFinal) {
      if (pos === 1) {
        return {
          bg: 'white',
          text: SWIM_GOLD,
          border: SWIM_GOLD,
          innerBg: 'white',
          innerText: SWIM_GOLD,
          rowBg: `linear-gradient(to right, ${SWIM_GOLD_LIGHT}, transparent)`
        };
      } else if (pos === 2) {
        return {
          bg: 'white',
          text: '#C0C0C0',
          border: '#C0C0C0',
          innerBg: 'white',
          innerText: '#C0C0C0',
          rowBg: 'linear-gradient(to right, rgba(192, 192, 192, 0.08), transparent)'
        };
      } else if (pos === 3) {
        return {
          bg: 'white',
          text: '#CD7F32',
          border: '#CD7F32',
          innerBg: 'white',
          innerText: '#CD7F32',
          rowBg: 'linear-gradient(to right, rgba(205, 127, 50, 0.08), transparent)'
        };
      } else if (pos >= 4 && pos <= 8) {
        return {
          bg: 'white',
          text: VUKZ.primary,
          border: VUKZ.primary,
          innerBg: 'white',
          innerText: VUKZ.primary,
          rowBg: 'transparent'
        };
      }
    } else if (isSemifinal && pos <= 8) {
      return {
        bg: 'white',
        text: VUKZ.primary,
        border: VUKZ.primary,
        innerBg: 'white',
        innerText: VUKZ.primary,
        rowBg: 'transparent'
      };
    } else if (pos <= 16) {
      return {
        bg: 'white',
        text: VUKZ.primary,
        border: VUKZ.primary,
        innerBg: 'white',
        innerText: VUKZ.primary,
        rowBg: 'transparent'
      };
    }
    
    return {
      bg: 'white',
      text: '#6b7280',
      border: '#d1d5db',
      innerBg: 'white',
      innerText: '#6b7280',
      rowBg: 'transparent'
    };
  };
  
  if (loading && !competition) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" 
               style={{ borderColor: VUKZ.primary }}></div>
          <p className="text-lg font-semibold" style={{ color: VUKZ.primary }}>
            {T.loadingComp}
          </p>
        </div>
      </div>
    );
  }
  
  if (error && !competition) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-semibold mb-2">{T.errorComp}</p>
          <p className="text-sm text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/competicoes')}
            className="mt-4 px-6 py-2 rounded-lg font-semibold transition-colors"
            style={{ backgroundColor: VUKZ.primary, color: 'white' }}
          >
            {T.backComps}
          </button>
        </div>
      </div>
    );
  }
  
  // Subtitle with proper fallback (avoid "undefined • undefined")
  const subtitleParts = [
    competition?.name,
    selectedYear,
    competition?.location
  ].filter(Boolean);
  const pageSubtitle = subtitleParts.length > 0 
    ? subtitleParts.join(' • ')
    : T.compSubtitle;
  
  return (
    <div className="min-h-screen pb-12" style={{ backgroundColor: VUKZ.bg }}>
      
      {/* Breadcrumb Fixo de Navegação */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-3 text-sm text-gray-500">
          <img src="/vukz-logo.png" alt="VUKZ" className="h-6 w-auto"
            onError={(e) => { e.target.style.display = 'none'; }} />
          <button
            onClick={() => navigate('/competicoes')}
            className="hover:text-gray-900 font-medium"
          >
            {T.competitions}
          </button>
          <span>/</span>
          <span className="font-bold text-gray-900">{getCompetitionDisplayName(competition?.name)}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* Header */}
        <SwimmingCompetitionHeader 
          competition={competition}
          year={selectedYear}
          events={events}
          allCompetitions={[]}
        />
        
        {/* Tabs de Navegação */}
        <div className="championship-tabs-nav flex items-center gap-1 mb-6">
          <button
            onClick={() => setActiveTab('results')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
              activeTab === 'results' 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {T.tabResults}
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
              activeTab === 'stats' 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {T.tabStats}
          </button>
          {/* Botão PDF — só aparece na aba Stats */}
          {activeTab === 'stats' && (
            <div className="ml-auto">
              <ChampionshipPdfExport
                competitionName={competition?.name}
                year={selectedYear}
                printing={printAll}
                onBeforePrint={() => setPrintAll(true)}
                onAfterPrint={() => setPrintAll(false)}
              />
            </div>
          )}
        </div>

        {/* Conteúdo da Aba Selecionada */}
        {activeTab === 'results' && (
          <div>
          {/* Filtros Compactos */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5 space-y-3">
            
            {/* Linha 1: Ano + Gênero + Tipo (horizontal, espaçados) */}
            <div className="flex items-center gap-6 flex-wrap">
              {/* Ano */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500 uppercase">{T.labelYear}</span>
                <select
                  value={selectedYear}
                  onChange={async (e) => {
                    const newYear = Number(e.target.value);
                    setSelectedYear(newYear);
                    // Find competition with same name in new year
                    try {
                      const comps = await swimmingService.getCompetitions({ year: newYear });
                      const currentName = (competition?.name || '').replace(/\d{4}/g, '').trim().toLowerCase();
                      const match = comps.find(c => {
                        const cName = (c.name || '').replace(/\d{4}/g, '').trim().toLowerCase();
                        return cName === currentName || cName.includes(currentName) || currentName.includes(cName);
                      });
                      if (match) {
                        navigate(`/natacao/competicao/${match.id}/${newYear}`);
                      } else {
                        navigate(`/natacao/competicao/${id}/${newYear}`);
                      }
                    } catch {
                      navigate(`/natacao/competicao/${id}/${newYear}`);
                    }
                  }}
                  className="px-3 py-1.5 text-sm font-bold border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="w-px h-6 bg-gray-200" />

              {/* Gênero */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-500 uppercase mr-1">{T.labelGender}</span>
                {['Feminino', 'Masculino'].map(g => (
                  <button
                    key={g}
                    onClick={() => setSelectedGender(g)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                      selectedGender === g
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {g === 'Feminino' ? 'Fem' : 'Masc'}
                  </button>
                ))}
              </div>

              <div className="w-px h-6 bg-gray-200" />

              {/* Tipo */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-500 uppercase mr-1">{T.labelType}</span>
                {[{ value: 'individual', label: T.individual }, { value: 'relay', label: T.relay }].map(t => (
                  <button
                    key={t.value}
                    onClick={() => setSelectedRelayType(t.value)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                      selectedRelayType === t.value
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Linha 2: Botões de Estilo */}
            <div className="border-t border-gray-100 pt-3 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-bold text-gray-500 uppercase">{T.labelStroke}</span>
              <div className="flex gap-1.5 flex-wrap">
                {availableStrokes.length === 0 ? (
                  <span className="text-sm text-gray-400">{T.noEvents}</span>
                ) : (
                  availableStrokes.map(stroke => (
                    <button
                      key={stroke}
                      onClick={() => setSelectedStroke(stroke)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                        selectedStroke === stroke
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {stroke}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Linha 3: Botões de Distância (para o estilo selecionado) */}
            {availableDistances.length > 0 && (
              <div className="border-t border-gray-100 pt-3 flex items-center gap-3 flex-wrap">
                <span className="text-xs font-bold text-gray-500 uppercase">{T.labelDist}</span>
                <div className="flex gap-1.5 flex-wrap">
                  {availableDistances.map(({ eventId, distance, label }) => (
                    <button
                      key={eventId}
                      onClick={() => setSelectedEvent(eventId)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                        selectedEvent === eventId
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {label || `${distance}m`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Linha 4: Fase */}
            {selectedEventPhases && selectedEventPhases.length > 0 && (
              <div className="border-t border-gray-100 pt-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-500 uppercase">{T.labelPhase}</span>
                <div className="flex gap-1.5 flex-wrap">
                  {selectedEventPhases.map(phase => {
                    const isActive = selectedRound === phase;
                    return (
                      <button
                        key={phase}
                        onClick={() => setSelectedRound(phase)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                          isActive 
                            ? 'bg-gray-900 text-white shadow-sm' 
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {phase}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          {/* Tabela de Resultados — Full Width, destaque */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-3"></div>
                  <p className="text-sm font-semibold text-gray-500">{T.loading}</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 font-semibold mb-2">{T.errorResults}</p>
                <p className="text-sm text-gray-500">{error}</p>
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-700 font-bold text-lg mb-1">{T.noResults}</p>
                <p className="text-sm text-gray-500">{T.noResultsSub}</p>
              </div>
            ) : (() => {
              // Agrupar por série se for eliminatória
              const isEliminatoria = selectedRound && (selectedRound.includes('liminat') || selectedRound.includes('Eliminat'));
              const heats = {};
              if (isEliminatoria) {
                (Array.isArray(results) ? results : []).forEach(r => {
                  const h = r.heat_number || 0;
                  if (!heats[h]) heats[h] = [];
                  heats[h].push(r);
                });
              }
              const heatKeys = Object.keys(heats).map(Number).sort((a, b) => a - b);
              const groups = isEliminatoria && heatKeys.length > 1
                ? heatKeys.map(h => ({ label: `Série ${h}`, rows: heats[h] }))
                : [{ label: null, rows: Array.isArray(results) ? results : [] }];

              return groups.map((group, gi) => (
                <div key={gi}>
                  {group.label && (
                    <div className="bg-gray-100 border-b border-gray-200 px-4 py-2">
                      <span className="text-sm font-black text-gray-600 uppercase tracking-wider">{T.heat} {group.label.replace(/Série |Heat /, '')}</span>
                      <span className="ml-2 text-xs text-gray-400">({group.rows.length} atletas)</span>
                    </div>
                  )}
                  <table className="w-full">
                    {gi === 0 && (
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-14">{T.colPos}</th>
                          {isEliminatoria && <th className="text-center py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-14">{T.colLane}</th>}
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{T.colAthlete}</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{T.colClub}</th>
                          <th className="text-right py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-32">{T.colTime}</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-16">{T.colPts}</th>
                          <th className="text-right py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-24">{T.colDiff}</th>
                        </tr>
                      </thead>
                    )}
                    {gi > 0 && group.label && (
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-14">{T.colPos}</th>
                          {isEliminatoria && <th className="text-center py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-14">{T.colLane}</th>}
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{T.colAthlete}</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{T.colClub}</th>
                          <th className="text-right py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-32">{T.colTime}</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-16">{T.colPts}</th>
                          <th className="text-right py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider w-24">{T.colDiff}</th>
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {group.rows.map((result, index) => {
                        const position = result.position || index + 1;
                        const isTop3 = position <= 3;
                    
                        return (
                          <tr 
                            key={index}
                            className={`border-b border-gray-100 transition-colors hover:bg-gray-50 ${isTop3 ? 'bg-gray-50/50' : ''}`}
                          >
                            {/* Posição */}
                            <td className="py-3.5 px-4">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-black ${
                                position === 1 ? 'bg-yellow-100 text-yellow-800' :
                                position === 2 ? 'bg-gray-200 text-gray-700' :
                                position === 3 ? 'bg-orange-100 text-orange-800' :
                                'text-gray-500'
                              }`}>
                                {position}
                              </span>
                            </td>
                        
                            {/* Raia */}
                            {isEliminatoria && (
                              <td className="py-3.5 px-4 text-center">
                                <span className="text-sm font-semibold text-gray-500">{result.lane || '—'}</span>
                              </td>
                            )}

                            {/* Atleta */}
                            <td className="py-3.5 px-4">
                              <span className={`font-bold ${isTop3 ? 'text-gray-900 text-base' : 'text-gray-800'}`}>
                                {result.swimmer_display_name || result.swimmer_name}
                              </span>
                            </td>
                        
                            {/* Clube */}
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-2">
                                {result.is_national_team === true && result.nationality ? (
                                  <img 
                                    src={`/flags/${result.nationality}.svg`}
                                    alt={result.nationality}
                                    className="w-6 h-4 object-cover rounded-sm border border-gray-200"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <img 
                                    src={result.club_logo_url || '/logos-natacao/default-club.svg'}
                                    alt={result.club_name || 'Clube'}
                                    className="w-6 h-6 rounded-full border border-gray-200 object-contain bg-white"
                                    onError={(e) => { e.target.onerror = null; e.target.src = '/logos-natacao/default-club.svg'; }}
                                  />
                                )}
                                <span className="text-sm text-gray-600">{result.club_name || 'Independente'}</span>
                              </div>
                            </td>
                        
                            {/* Tempo + Tags de Recorde */}
                            <td className="py-3.5 px-4 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {result.is_world_record ? <span className="px-1.5 py-0.5 text-[10px] font-black bg-red-600 text-white rounded">RM</span> : null}
                                {result.is_national_record ? <span className="px-1.5 py-0.5 text-[10px] font-black bg-green-700 text-white rounded">RB</span> : null}
                                {result.is_event_record ? <span className="px-1.5 py-0.5 text-[10px] font-black bg-blue-700 text-white rounded">RC</span> : null}
                                <span className={`font-black tabular-nums ${isTop3 ? 'text-xl text-gray-900' : 'text-lg text-gray-800'}`}>
                                  {formatSwimmingTimeRobust(result)}
                                </span>
                              </div>
                            </td>

                            {/* Pontos */}
                            <td className="py-3.5 px-4 text-center">
                              {result.points > 0 ? (
                                <span className="text-sm font-bold text-gray-700">{result.points}</span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                        
                            {/* Diferença */}
                            <td className="py-3.5 px-4 text-right">
                              {result.time_diff !== null && result.time_diff !== undefined && parseFloat(result.time_diff) > 0 ? (
                                <span className="text-sm font-semibold text-gray-400 tabular-nums">
                                  +{parseFloat(result.time_diff).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ));
            })()}
          </div>
          </div>
        )}
        
        {(activeTab === 'stats' || printAll) && (
          <Suspense fallback={<div className="p-8 text-center text-gray-400">{T.loadingStats}</div>}>
            <ChampionshipStatsPanel
              competitionId={id ? Number(id) : null}
              competitionName={competition?.name}
              events={filteredEvents}
              printAll={printAll}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default SwimmingChampionshipDetailsPage;
